import { clsx } from 'clsx';
import { useState, useEffect, useRef, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LogoFullColorImgSrc from '../../../../../assets/images/sendit-logo-full-color.svg';
import LogoFullWhiteColorImgSrc from '../../../../../assets/images/sendit-logo-full-white-color.svg';
import {
  ProviderCompletedRegistrationStepsEnum,
  ProviderOnboarding,
  OnboardingProviderService,
} from '../../../../../js/api';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { useCodeDefinitionsByCategories } from '../../../hooks/useCodeDefinitions';
import { APP_BG_COLOR } from '../../../utils/globalStyles';
import { BasicInfo, BasicInfoRef } from './BasicInfo';
import { MarketplaceAgreement } from './MarketplaceAgreement';
import { ProfessionalDetails, ProfessionalDetailsRef } from './ProfessionalDetails';
import { Rates, RatesRef } from './Rates';
import { StepNavigation, steps } from './StepNavigation';
import { transformFormData } from './transformers';
import { OnboardingFormData } from './types';

const handleCompletedStepsNavigation = (
  completedSteps: ProviderCompletedRegistrationStepsEnum[],
  setCurrentStep: (step: number) => void,
  setIsRedirecting: (isRedirecting: boolean) => void
) => {
  const allStepsCompleted = Object.values(ProviderCompletedRegistrationStepsEnum).every((step) =>
    completedSteps.includes(step)
  );

  if (allStepsCompleted) {
    setIsRedirecting(true);
    setTimeout(() => {
      window.location.href = '/app/provider/home';
    }, 500);
    return true;
  }

  const missingSteps = Object.values(ProviderCompletedRegistrationStepsEnum).filter(
    (step) => !completedSteps.includes(step)
  );

  if (missingSteps.length > 0) {
    setCurrentStep(steps.findIndex((s) => s.value === missingSteps[0]) + 1);
  }
  return false;
};

export const ProviderOnboardingWrapper = () => {
  useDocumentTitle('Provider Onboarding');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBasicInfoErrors, setShowBasicInfoErrors] = useState(false);
  const [providerData, setProviderData] = useState<ProviderOnboarding | null>(null);
  const { user, isLoading: isLoadingUser } = useCurrentUser();
  const providerId = user?.provider_id;
  const basicInfoRef = useRef<BasicInfoRef>(null);
  const ratesRef = useRef<RatesRef>(null);
  const professionalDetailsRef = useRef<ProfessionalDetailsRef>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Fetch all code definitions using the centralized manager
  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories([
    'PostalStateChoices',
    'ProviderRateFields',
    'WorkType',
    'TravelType',
    'LicenseStateChoices',
    'BoardCertificationType',
    'Degree',
    'Specialty',
    'CertificationType',
  ]);

  // Map to the existing apiData structure for compatibility
  const apiData = useMemo(
    () => ({
      postalStates: codeDefinitionsByCategory?.PostalStateChoices || [],
      rates: codeDefinitionsByCategory?.ProviderRateFields || [],
      workTypes: codeDefinitionsByCategory?.WorkType || [],
      travelTypes: codeDefinitionsByCategory?.TravelType || [],
      licenseStateOptions: codeDefinitionsByCategory?.LicenseStateChoices || [],
      boardCerts: codeDefinitionsByCategory?.BoardCertificationType || [],
      degrees: codeDefinitionsByCategory?.Degree || [],
      specialties: codeDefinitionsByCategory?.Specialty || [],
      lifeSupportCerts: codeDefinitionsByCategory?.CertificationType || [],
    }),
    [codeDefinitionsByCategory]
  );

  const codeDefinitions = useMemo(
    () => ({
      postalStateOptions: apiData.postalStates.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
      providerRateOptions: apiData.rates.map((opt) => ({ value: opt.code, label: opt.title })),
      workTypeOptions: apiData.workTypes.map((opt) => ({ value: opt.code, label: opt.title })),
      travelTypeOptions: apiData.travelTypes.map((opt) => ({ value: opt.code, label: opt.title })),
      licenseStateOptions: apiData.licenseStateOptions.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
      boardCertificationOptions: apiData.boardCerts.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
      degreeOptions: apiData.degrees.map((opt) => ({ value: opt.code, label: opt.title })),
      specialtyOptions: apiData.specialties.map((opt) => ({ value: opt.code, label: opt.title })),
      lifeSupportCertificationOptions: apiData.lifeSupportCerts.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
    }),
    [apiData]
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const initialFormData = useMemo(() => {
    if (!providerData || !apiData) return null;

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    const birthDate =
      providerData.birth_date && dateRegex.test(providerData.birth_date)
        ? providerData.birth_date
            .split('-')
            .slice(1)
            .concat(providerData.birth_date.split('-')[0])
            .join('/')
        : '';

    return {
      1: {
        firstName: providerData.first_name || '',
        lastName: providerData.last_name || '',
        dateOfBirth: birthDate,
        npiNumber: providerData.npi || '',
        streetAddress: providerData.street_address || '',
        city: providerData.city || '',
        state: providerData.state || '',
        zip: providerData.zip_code || '',
      },
      2: {
        shiftRateDay: providerData.shift_rate_day,
        shiftRateNight: providerData.shift_rate_night,
        shiftRateCall: providerData.shift_rate_call,
        shiftRate24h: providerData.shift_rate_24h,
        travelType: providerData.travel_type || '',
        workType: providerData.work_type || '',
      },
      3: {
        taxonomies: providerData.metadata?.nppes?.taxonomies || [],
        licensedStates:
          providerData.licenses?.map((license) => ({
            value: license.state,
            label:
              apiData.licenseStateOptions.find((opt) => opt.code === license.state)?.title ||
              license.state,
          })) || [],
        isResident: providerData.is_resident || false,
        estimatedGraduationDate: providerData.resident_graduation_on
          ? new Date(providerData.resident_graduation_on)
          : null,
        hasCompactLicense: providerData.has_imlc || false,
        degree:
          providerData.degree?.map((d) => ({
            value: d,
            label: apiData.degrees.find((opt) => opt.code === d)?.title || d,
          })) || [],
        specialties:
          providerData.specialties?.map((s) => ({
            value: s,
            label: apiData.specialties.find((opt) => opt.code === s)?.title || s,
          })) || [],
        boardCertifications:
          providerData.board_certifications?.map((bc) => ({
            value: bc.type,
            label: apiData.boardCerts.find((opt) => opt.code === bc.type)?.title || bc.type,
            boardEligible: bc.board_eligible,
          })) || [],
        lifeSupportCertifications:
          providerData.certifications?.map((c) => ({
            value: c.type,
            label: apiData.lifeSupportCerts.find((opt) => opt.code === c.type)?.title || c.type,
          })) || [],
      },
      4: {
        agreedToTerms: !!providerData.marketplace_agreement_at,
        signature: providerData.metadata?.signature || '',
      },
    };
  }, [providerData, apiData]);

  useEffect(() => {
    const fetchProviderData = async () => {
      if (!providerId) {
        setIsInitialLoading(false);
        return;
      }

      try {
        const providerData = await OnboardingProviderService.onboardingProviderRetrieve({
          id: providerId,
        });

        setProviderData(providerData);
        handleCompletedStepsNavigation(
          providerData.completed_registration_steps || [],
          setCurrentStep,
          setIsRedirecting
        );
      } catch (error) {
        console.error('Error fetching provider data:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (!isLoadingUser) {
      fetchProviderData();
    }
  }, [providerId, isLoadingUser]);

  const saveFormData = async (formData: OnboardingFormData) => {
    if (!providerId) {
      console.error('No provider ID found');
      return;
    }

    try {
      setIsSubmitting(true);
      const transformedData = {
        ...transformFormData(formData),
        registration_step: steps[currentStep - 1].value as ProviderCompletedRegistrationStepsEnum,
      };
      const updatedData = await OnboardingProviderService.onboardingProviderPartialUpdate({
        id: providerId,
        requestBody: transformedData,
      });

      setProviderData(updatedData);
      return updatedData;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMarketplaceAgreementCompleted = useMemo(() => {
    return providerData?.completed_registration_steps?.includes(
      ProviderCompletedRegistrationStepsEnum.SIGN_AGREEMENT
    );
  }, [providerData?.completed_registration_steps]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleStepNavigation = async (targetStep: number) => {
    // Handle marketplace agreement step (step 4) navigation
    const isMarketplaceStep = currentStep === 4;

    // Special handling for marketplace step
    if (isMarketplaceStep) {
      // Allow backward navigation freely
      if (targetStep < currentStep) {
        setCurrentStep(targetStep);
        return;
      }

      // Handle forward navigation when agreement isn't completed
      if (targetStep > currentStep && !isMarketplaceAgreementCompleted) {
        handleAgreeToMarketplace();
        return;
      }

      // If agreement is completed, handle navigation
      if (isMarketplaceAgreementCompleted) {
        handleCompletedStepsNavigation(
          providerData?.completed_registration_steps || [],
          setCurrentStep,
          setIsRedirecting
        );
        return;
      }

      // Default case for marketplace step: allow navigation
      setCurrentStep(targetStep);
      return;
    }

    // Check for fatal errors in the current step
    const currentStepRef =
      currentStep === 1
        ? basicInfoRef.current
        : currentStep === 2
          ? ratesRef.current
          : professionalDetailsRef.current;

    // Check for fatal errors before attempting submission
    if (currentStep === 1) {
      setShowBasicInfoErrors(true); // This will trigger validation display
      if (basicInfoRef.current?.hasFatalErrors) {
        const fatalErrors = Object.entries(basicInfoRef.current.validationMessages || {})
          .filter(([_, message]) => message.colorClass.includes('danger'))
          .map(([field, message]) => message.message)
          .join(', ');

        toast.error(
          <>
            Please fix the following errors:
            <br />
            {fatalErrors}
          </>
        );
        return; // Don't proceed with submission if there are fatal errors
      }
    }

    try {
      const formData = currentStepRef?.formData;
      if (formData) {
        const updatedProvider = await saveFormData(formData);
        if (!updatedProvider) {
          // If saveFormData returns null/undefined, don't proceed
          return;
        }

        // Check completion status using the updated provider data
        if (updatedProvider.completed_registration_steps?.length === steps.length) {
          handleCompletedStepsNavigation(
            updatedProvider.completed_registration_steps || [],
            setCurrentStep,
            setIsRedirecting
          );
          return;
        }

        // Only navigate if we successfully saved the data
        setCurrentStep(targetStep);
      }
    } catch (error) {
      handleApiError(error, { messagePrefix: 'Error saving form data' });
    }
  };

  const handleExit = () => {
    window.location.href = '/app/provider/home';
  };

  const handleAgreeToMarketplace = async () => {
    if (!providerId) return;

    try {
      setIsSubmitting(true);
      const updatedProvider =
        await OnboardingProviderService.onboardingProviderMarketplaceAgreementCreate({
          id: providerId,
          xTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          requestBody: {} as any,
        });

      handleCompletedStepsNavigation(
        updatedProvider.completed_registration_steps || [],
        setCurrentStep,
        setIsRedirecting
      );
    } catch (error) {
      console.error('Error submitting agreement:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepInfo = steps[currentStep - 1];

  return (
    <div className={clsx('tw:flex tw:flex-col tw:min-h-screen', APP_BG_COLOR)}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="tw:flex tw:justify-center tw:m-4">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div onClick={handleExit} className="tw:cursor-pointer">
          <img src={LogoFullColorImgSrc} alt="Sendit logo" className="tw:h-16 tw:dark:hidden" />
          <img
            src={LogoFullWhiteColorImgSrc}
            alt="Sendit logo"
            className="tw:h-16 tw:hidden tw:dark:block"
          />
        </div>
      </div>
      <div className="tw:max-w-7xl tw:mx-auto tw:w-full tw:flex-grow">
        <div className="tw:lg:flex tw:lg:gap-8 tw:lg:p-4">
          <StepNavigation
            currentStep={currentStep}
            triggerFormAction={handleStepNavigation}
            completedSteps={providerData?.completed_registration_steps || []}
          />
          <div className="tw:lg:flex-1 tw:lg:m-0 tw:h-full">
            {isInitialLoading ? (
              <div className="tw:flex tw:items-center tw:justify-center tw:h-64">
                <div className="tw:text-gray-500">Loading...</div>
              </div>
            ) : (
              <div className="tw:bg-white tw:m-4 tw:mt-0 tw:rounded-lg tw:border-1 tw:border-zinc-200 tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:h-full">
                <div className="tw:max-w-xl tw:mx-auto">
                  <div>
                    {currentStep === 1 && initialFormData && (
                      <BasicInfo
                        ref={basicInfoRef}
                        stepInfo={currentStepInfo}
                        initialData={initialFormData[1]}
                        registrationSteps={providerData?.metadata?.registration_steps}
                        stateOptions={codeDefinitions?.postalStateOptions || []}
                        nppesData={providerData?.metadata?.nppes}
                        showFatalErrors={showBasicInfoErrors}
                      />
                    )}
                  </div>
                  <div>
                    {currentStep === 2 && (
                      <Rates
                        ref={ratesRef}
                        stepInfo={currentStepInfo}
                        initialData={initialFormData?.[2]}
                        registrationSteps={providerData?.metadata?.registration_steps}
                        providerRateOptions={codeDefinitions?.providerRateOptions || []}
                        workTypeOptions={codeDefinitions?.workTypeOptions || []}
                        travelTypeOptions={codeDefinitions?.travelTypeOptions || []}
                      />
                    )}
                  </div>
                  <div>
                    {currentStep === 3 && (
                      <ProfessionalDetails
                        ref={professionalDetailsRef}
                        stepInfo={currentStepInfo}
                        initialData={initialFormData?.[3]}
                        registrationSteps={providerData?.metadata?.registration_steps}
                        licenseStateOptions={codeDefinitions?.licenseStateOptions || []}
                        boardCertificationOptions={codeDefinitions?.boardCertificationOptions || []}
                        degreeOptions={codeDefinitions?.degreeOptions || []}
                        specialtyOptions={codeDefinitions?.specialtyOptions || []}
                        lifeSupportCertificationOptions={
                          codeDefinitions?.lifeSupportCertificationOptions || []
                        }
                      />
                    )}
                  </div>
                  <div>
                    {currentStep === 4 && (
                      <MarketplaceAgreement
                        stepInfo={currentStepInfo}
                        providerId={providerId}
                        agreementDate={providerData?.marketplace_agreement_at}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="tw:flex tw:justify-between tw:mx-4 tw:mb-4">
              <Button
                plain
                className="tw:bg-white tw:border-1 tw:border-zinc-200 tw:hover:bg-gray-50 tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:dark:hover:!bg-zinc-500"
                onClick={handleExit}
                disabled={isSubmitting}
              >
                Finish Later
              </Button>
              <div>
                {currentStep > 1 && (
                  <Button
                    plain
                    className="tw:bg-white tw:border-1 tw:border-zinc-200 tw:hover:bg-gray-50 tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:dark:hover:!bg-zinc-500"
                    onClick={() => handleStepNavigation(currentStep - 1)}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                )}
                <Button
                  plain
                  className="tw:!ml-4 tw:bg-brand-green-1 tw:!text-white tw:hover:!bg-brand-green-0 tw:dark:hover:!bg-brand-green-0"
                  onClick={() => handleStepNavigation(currentStep + 1)}
                  disabled={isSubmitting}
                >
                  {currentStep === 4
                    ? isSubmitting
                      ? 'Agreeing...'
                      : isMarketplaceAgreementCompleted
                        ? 'Next'
                        : 'I Agree'
                    : 'Save and Next'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isRedirecting && (
        <div className="tw:fixed tw:inset-0 tw:bg-black/50 tw:flex tw:items-center tw:justify-center tw:z-50">
          <div className="tw:bg-white tw:dark:bg-zinc-800 tw:p-6 tw:rounded-lg tw:shadow-xl tw:text-center">
            <div className="tw:animate-spin tw:rounded-full tw:h-12 tw:w-12 tw:border-4 tw:border-zinc-300 tw:border-t-brand-green-1 tw:mx-auto tw:mb-4" />
            <p className="tw:text-zinc-700 tw:dark:text-zinc-200">Redirecting to your profile...</p>
          </div>
        </div>
      )}
    </div>
  );
};
